/**
 * EmailChatModal — Interactive AI chat for email review.
 * 
 * ENHANCED: Left panel now shows FULL EMAIL THREAD (all messages in the conversation)
 * Right panel: Chat with Claude to understand and respond to emails
 * 
 * Features:
 * - Full email thread display (all messages in conversation)
 * - Visual distinction between outbound (us) and inbound (vendor) messages
 * - Chat history with AI (now has full thread context)
 * - Draft email preview and editing
 * - Send response directly
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { chatWithEmail, getFullEmail, sendEmailResponse, saveTrainingNote } from '../../api/email';

// ---------------------------------------------------------------------------
// Thread message component - displays a single message in the conversation
// ---------------------------------------------------------------------------

function ThreadMessage({ message, isLast }) {
  const [expanded, setExpanded] = useState(isLast); // Auto-expand last message
  const isOutbound = message.is_outbound;
  
  // Truncate body for collapsed view
  const MAX_COLLAPSED_LENGTH = 200;
  const needsTruncation = (message.body?.length || 0) > MAX_COLLAPSED_LENGTH;
  const displayBody = expanded 
    ? message.body 
    : (message.body?.substring(0, MAX_COLLAPSED_LENGTH) + (needsTruncation ? '...' : ''));
  
  return (
    <div className={`p-3 rounded-lg mb-2 ${
      isOutbound 
        ? 'bg-blue-50 border-l-4 border-blue-400' 
        : 'bg-gray-50 border-l-4 border-gray-300'
    }`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
            isOutbound 
              ? 'bg-blue-200 text-blue-800' 
              : 'bg-gray-200 text-gray-700'
          }`}>
            {isOutbound ? '➤ WE SENT' : '◄ VENDOR'}
          </span>
          <span className="text-xs text-gray-500">{message.formatted_date || message.date}</span>
        </div>
        {needsTruncation && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-600 mb-1">
        From: <span className="font-medium">{message.from}</span>
      </p>
      <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
        {displayBody || '(no body)'}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat message component
// ---------------------------------------------------------------------------

function ChatMessage({ role, content, draft }) {
  const isUser = role === 'user';
  
  // Helper to render special blocks
  const renderContent = (text) => {
    // Handle draft blocks
    if (text.includes('---DRAFT EMAIL---')) {
      return text.split('---DRAFT EMAIL---').map((part, i) => {
        if (i === 0) {
          return <span key={i}>{renderContent(part)}</span>;
        }
        const [draftContent, afterDraft] = part.split('---END DRAFT---');
        return (
          <div key={i}>
            <div className="my-2 p-3 bg-white border-2 border-blue-300 rounded-lg">
              <p className="text-xs font-semibold text-blue-600 mb-1">📧 Draft Email</p>
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                {draftContent.trim()}
              </pre>
            </div>
            {afterDraft && <span>{renderContent(afterDraft)}</span>}
          </div>
        );
      });
    }
    
    // Handle training note blocks
    if (text.includes('---TRAINING NOTE---')) {
      return text.split('---TRAINING NOTE---').map((part, i) => {
        if (i === 0) {
          return <span key={i}>{part.trim() && <p className="text-sm whitespace-pre-wrap">{part.trim()}</p>}</span>;
        }
        const [noteContent, afterNote] = part.split('---END TRAINING NOTE---');
        return (
          <div key={i}>
            <div className="my-2 p-3 bg-white border-2 border-purple-300 rounded-lg">
              <p className="text-xs font-semibold text-purple-600 mb-1">🧠 Training Note</p>
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                {noteContent.trim()}
              </pre>
            </div>
            {afterNote && afterNote.trim() && <p className="text-sm whitespace-pre-wrap">{afterNote.trim()}</p>}
          </div>
        );
      });
    }
    
    // Plain text
    return <p className="text-sm whitespace-pre-wrap">{text.trim()}</p>;
  };
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[85%] rounded-lg px-4 py-2 ${
        isUser 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-100 text-gray-900'
      }`}>
        {renderContent(content)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Draft preview panel
// ---------------------------------------------------------------------------

function DraftPreview({ draft, onEdit, onSend, sending }) {
  const [editMode, setEditMode] = useState(false);
  const [editedSubject, setEditedSubject] = useState(draft.subject);
  const [editedBody, setEditedBody] = useState(draft.body);
  // Tactical Fix B — PDF attachment state, lives with the draft.
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setEditedSubject(draft.subject);
    setEditedBody(draft.body);
  }, [draft]);

  // Tactical Fix B — attachment handling
  const MAX_FILE_BYTES = 10 * 1024 * 1024;

  function handleFilesSelected(e) {
    const incoming = Array.from(e.target.files || []);
    const valid = [];
    const errors = [];
    for (const f of incoming) {
      const isPdf = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        errors.push(`${f.name}: only PDF files are allowed`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        errors.push(`${f.name}: exceeds the 10MB per-file limit`);
        continue;
      }
      valid.push(f);
    }
    if (errors.length > 0) {
      alert(errors.join('\n'));
    }
    setFiles(prev => [...prev, ...valid]);
    // Allow re-selecting the same file later
    e.target.value = '';
  }

  function handleRemoveFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (editMode) {
    return (
      <div className="border-t border-gray-200 p-4 bg-blue-50">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-blue-800">✏️ Edit Draft</p>
          <button
            onClick={() => {
              onEdit({ subject: editedSubject, body: editedBody });
              setEditMode(false);
            }}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Done editing
          </button>
        </div>
        <input
          type="text"
          value={editedSubject}
          onChange={e => setEditedSubject(e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded mb-2"
          placeholder="Subject"
        />
        <textarea
          value={editedBody}
          onChange={e => setEditedBody(e.target.value)}
          rows={6}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded font-sans"
          placeholder="Email body"
        />
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 p-4 bg-green-50">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-green-800">📧 Ready to Send</p>
        <div className="flex gap-2">
          <button
            onClick={() => setEditMode(true)}
            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
          >
            Edit
          </button>
          <button
            onClick={() => onSend(editedSubject, editedBody, files)}
            disabled={sending}
            className="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {sending
              ? 'Sending...'
              : (files.length > 0
                  ? `Send Email + ${files.length} PDF${files.length > 1 ? 's' : ''}`
                  : 'Send Email')}
          </button>
        </div>
      </div>
      <div className="bg-white border border-green-200 rounded p-3">
        <p className="text-xs text-gray-500 mb-1">Subject:</p>
        <p className="text-sm font-medium text-gray-900 mb-2">{editedSubject}</p>
        <p className="text-xs text-gray-500 mb-1">Body:</p>
        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{editedBody}</pre>

        {/* Tactical Fix B — PDF attachments */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500">Attachments (PDF only, max 10MB each)</p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              + Attach PDF
            </button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,application/pdf"
              multiple
              onChange={handleFilesSelected}
              className="hidden"
            />
          </div>
          {files.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No attachments</p>
          ) : (
            <ul className="space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-xs">
                  <span>📎</span>
                  <span className="flex-1 truncate text-gray-700">{f.name}</span>
                  <span className="text-gray-400">{formatBytes(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(i)}
                    className="text-red-500 hover:text-red-700 font-medium"
                    aria-label={`Remove ${f.name}`}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Training note preview panel
// ---------------------------------------------------------------------------

function TrainingNotePreview({ trainingNote, onSave, onDiscard, saving }) {
  const [editMode, setEditMode] = useState(false);
  const [editedVendor, setEditedVendor] = useState(trainingNote.vendor_name || '');
  const [editedPattern, setEditedPattern] = useState(trainingNote.sender_pattern || '');
  const [editedText, setEditedText] = useState(trainingNote.note_text || '');

  useEffect(() => {
    setEditedVendor(trainingNote.vendor_name || '');
    setEditedPattern(trainingNote.sender_pattern || '');
    setEditedText(trainingNote.note_text || '');
  }, [trainingNote]);

  if (editMode) {
    return (
      <div className="border-t border-purple-200 p-4 bg-purple-50">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-purple-800">✏️ Edit Training Note</p>
          <button
            onClick={() => setEditMode(false)}
            className="text-xs text-purple-600 hover:text-purple-800"
          >
            Done editing
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input
            type="text"
            value={editedVendor}
            onChange={e => setEditedVendor(e.target.value)}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            placeholder="Vendor name (optional)"
          />
          <input
            type="text"
            value={editedPattern}
            onChange={e => setEditedPattern(e.target.value)}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            placeholder="Sender pattern (optional)"
          />
        </div>
        <textarea
          value={editedText}
          onChange={e => setEditedText(e.target.value)}
          rows={5}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded font-sans"
          placeholder="Training instructions"
        />
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => onSave(editedVendor, editedPattern, editedText)}
            disabled={saving || !editedText.trim()}
            className="px-3 py-1 text-xs font-medium bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Training Note'}
          </button>
          <button
            onClick={onDiscard}
            className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
          >
            Discard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-purple-200 p-4 bg-purple-50">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-purple-800">🧠 AI Training Note</p>
        <div className="flex gap-2">
          <button
            onClick={() => setEditMode(true)}
            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
          >
            Edit
          </button>
          <button
            onClick={() => onSave(editedVendor, editedPattern, editedText)}
            disabled={saving}
            className="px-3 py-1 text-xs font-medium bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Note'}
          </button>
          <button
            onClick={onDiscard}
            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
          >
            Discard
          </button>
        </div>
      </div>
      <div className="bg-white border border-purple-200 rounded p-3">
        {editedVendor && (
          <div className="mb-2">
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              Vendor: {editedVendor}
            </span>
          </div>
        )}
        {editedPattern && (
          <div className="mb-2">
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
              Sender: {editedPattern}
            </span>
          </div>
        )}
        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{editedText}</pre>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal component
// ---------------------------------------------------------------------------

export default function EmailChatModal({ entryId, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [trainingNote, setTrainingNote] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  const [noteResult, setNoteResult] = useState(null);
  const [showThread, setShowThread] = useState(true); // Toggle between thread and single email view
  
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load full email on mount
  useEffect(() => {
    async function load() {
      try {
        const data = await getFullEmail(entryId);
        setEmail(data);
      } catch (err) {
        console.error('Failed to load email:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [entryId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on load
  useEffect(() => {
    if (!loading) {
      inputRef.current?.focus();
    }
  }, [loading]);

  const handleSendMessage = useCallback(async () => {
    if (!input.trim() || chatLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      // Build history from messages (exclude the new one we just added)
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      
      const result = await chatWithEmail(entryId, userMessage, history);
      
      setMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
      
      // If AI returned a draft, store it
      if (result.draft) {
        setDraft(result.draft);
      }
      
      // If AI returned a training note, store it
      if (result.training_note) {
        setTrainingNote(result.training_note);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  }, [input, chatLoading, messages, entryId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendEmail = async (subject, body, files = []) => {
    setSending(true);
    setSendResult(null);
    try {
      const result = await sendEmailResponse(entryId, subject, body, files);
      setSendResult({ success: true, message: result.message });
      onSent?.();
      // Close modal after 2 seconds
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setSendResult({ success: false, message: err?.response?.data?.error || 'Send failed' });
    } finally {
      setSending(false);
    }
  };

  const handleEditDraft = (newDraft) => {
    setDraft(newDraft);
  };

  const handleSaveTrainingNote = async (vendorName, senderPattern, noteText) => {
    setSavingNote(true);
    setNoteResult(null);
    try {
      const result = await saveTrainingNote(entryId, vendorName, senderPattern, noteText);
      setNoteResult({ success: true, message: 'Training note saved!' });
      setTrainingNote(null);
      // Add confirmation message to chat
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✅ Training note saved successfully! The AI will now apply these instructions when processing similar emails${vendorName ? ` from ${vendorName}` : ''}.`
      }]);
    } catch (err) {
      setNoteResult({ success: false, message: err?.response?.data?.error || 'Save failed' });
    } finally {
      setSavingNote(false);
    }
  };

  const handleDiscardTrainingNote = () => {
    setTrainingNote(null);
  };

  // Quick action buttons
  const quickActions = [
    { label: "What's this about?", prompt: "What is this vendor asking or saying? Give me a brief summary based on the full conversation." },
    { label: "Draft response", prompt: "Draft a professional response to this email addressing their questions or concerns." },
    { label: "Need more info", prompt: "Draft an email asking for more information or clarification." },
    { label: "Create training note", prompt: "Create a training note based on this email so the AI handles similar emails better in the future. Be specific about what patterns to look for and how to process them." },
  ];

  // Thread messages from API
  const threadMessages = email?.thread_messages || [];
  const hasThread = threadMessages.length > 1;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-gray-500">Loading email thread...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Email Response Assistant</h2>
            <p className="text-sm text-gray-500">
              {hasThread 
                ? `Full conversation (${threadMessages.length} messages) • AI has complete context`
                : 'Chat with AI to understand and respond'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Main content - two panels */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left panel - Email thread */}
          <div className="w-2/5 border-r border-gray-200 flex flex-col overflow-hidden">
            {/* Header with toggle */}
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {email?.matched_vendor_name && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                    {email.matched_vendor_name}
                  </span>
                )}
                {email?.matched_job_number && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                    Job #{email.matched_job_number}
                  </span>
                )}
                {hasThread && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                    {threadMessages.length} messages
                  </span>
                )}
                {email?.gmail_message_id && (
                  <a
                    href={`https://mail.google.com/mail/u/0/#inbox/${email.gmail_message_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Gmail
                  </a>
                )}
              </div>
              
              {/* Thread/Single toggle */}
              {hasThread && (
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => setShowThread(true)}
                    className={`px-2 py-1 text-xs rounded ${showThread ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Full Thread
                  </button>
                  <button
                    onClick={() => setShowThread(false)}
                    className={`px-2 py-1 text-xs rounded ${!showThread ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Current Email Only
                  </button>
                </div>
              )}
              
              <h3 className="font-medium text-gray-900">{email?.email_subject}</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {email?.email_received_at && new Date(email.email_received_at).toLocaleString()}
              </p>
            </div>
            
            {/* Thread messages or single email */}
            <div className="flex-1 overflow-y-auto p-4">
              {showThread && hasThread ? (
                // Show full thread
                <div>
                  <p className="text-xs text-gray-500 mb-3 font-medium">
                    📧 Email Conversation (oldest to newest)
                  </p>
                  {threadMessages.map((msg, idx) => (
                    <ThreadMessage 
                      key={idx} 
                      message={msg} 
                      isLast={idx === threadMessages.length - 1}
                    />
                  ))}
                </div>
              ) : (
                // Show single email
                <div>
                  <p className="text-xs text-gray-500 mb-2">
                    From: <span className="font-medium text-gray-700">{email?.email_sender}</span>
                  </p>
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                    {email?.email_body || '(No content)'}
                  </pre>
                </div>
              )}
            </div>

            {email?.ai_summary && (
              <div className="p-3 bg-amber-50 border-t border-amber-200">
                <p className="text-xs font-medium text-amber-800 mb-1">🤖 AI Analysis</p>
                <p className="text-xs text-amber-700">{email.ai_summary}</p>
              </div>
            )}
          </div>

          {/* Right panel - Chat interface */}
          <div className="w-3/5 flex flex-col overflow-hidden">
            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-white">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-8">
                  <div className="text-4xl mb-4">💬</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    How can I help with this email?
                  </h3>
                  <p className="text-sm text-gray-500 mb-2">
                    {hasThread 
                      ? `I have access to all ${threadMessages.length} messages in this conversation.`
                      : 'I can see the full email content and job details.'
                    }
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Ask me to explain what's happening, draft a response, or anything else.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {quickActions.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setInput(action.prompt);
                          inputRef.current?.focus();
                        }}
                        className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg, i) => (
                    <ChatMessage key={i} role={msg.role} content={msg.content} />
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start mb-3">
                      <div className="bg-gray-100 rounded-lg px-4 py-2">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </>
              )}
            </div>

            {/* Draft preview */}
            {draft && !sendResult?.success && (
              <DraftPreview
                draft={draft}
                onEdit={handleEditDraft}
                onSend={handleSendEmail}
                sending={sending}
              />
            )}

            {/* Training note preview */}
            {trainingNote && (
              <TrainingNotePreview
                trainingNote={trainingNote}
                onSave={handleSaveTrainingNote}
                onDiscard={handleDiscardTrainingNote}
                saving={savingNote}
              />
            )}

            {/* Note save result */}
            {noteResult && (
              <div className={`p-3 border-t ${noteResult.success ? 'bg-purple-50' : 'bg-red-50'}`}>
                <p className={`text-sm font-medium ${noteResult.success ? 'text-purple-800' : 'text-red-800'}`}>
                  {noteResult.success ? '✓ ' : '✗ '}{noteResult.message}
                </p>
              </div>
            )}

            {/* Send result */}
            {sendResult && (
              <div className={`p-4 border-t ${sendResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className={`text-sm font-medium ${sendResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {sendResult.success ? '✓ ' : '✗ '}{sendResult.message}
                </p>
              </div>
            )}

            {/* Input area */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
                  rows={2}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!input.trim() || chatLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
